import { useAppDispatch, useAppSelector } from "@/app/hooks"
import { useGetSessionEventsQuery, useGetPlayersQuery } from "../api/apiSlice"
import MultiEventText from "./MultiEventText"
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { addEvent, sendChatMessage } from "./multiworldSlice"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Settings2Icon } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Event } from "@/app/types"
import { ScrollArea } from "@/components/ui/scroll-area"
import { nanoid } from "@reduxjs/toolkit"

interface MultiEventViewerProps {
  sessionId: string
}

function MultiEventViewer(props: MultiEventViewerProps) {
  const { sessionId } = props
  const { isLoading, error: eventsError } = useGetSessionEventsQuery(sessionId)
  const { isLoading: playersLoading, data: players } =
    useGetPlayersQuery(sessionId)
  const multiworldEvents = useAppSelector(state => state.multiworld.events)
  const currentPlayer = useAppSelector(state => state.multiworld.player_id)
  const user = useAppSelector(state => state.user)
  const dispatch = useAppDispatch()
  const [hasScrolled, setHasScrolled] = useState(false)
  const [chatMessage, setChatMessage] = useState("")
  const [showSelfItems, setShowSelfItems] = useState(true)  
  const initComplete = useAppSelector(state => state.multiworld.init_complete)
  const [showSamePlayerItems, setShowSamePlayerItems] = useState(false)
  const [showOtherItems, setShowOtherItems] = useState(true)
  const [showChat, setShowChat] = useState(true)
  const [showSystem, setShowSystem] = useState(true)
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0)
  const [popupDismissed, setPopupDismissed] = useState(false)
  const flags = useAppSelector(state => state.multiworld.flags)

  const allCommands = useMemo(() => [
    { name: "/countdown", description: "Start a countdown timer", params: "[seconds]", example: "/countdown 5" },
    { name: "/missing", description: "Show unchecked locations", params: "", example: "/missing", disabled: !flags.missingCmd },
    { name: "/ready_check", description: "Start a ready check for all players", params: "", example: "/ready_check" },
    { name: "/cancel_ready", description: "Cancel the active ready check", params: "", example: "/cancel_ready" },
  ], [flags.missingCmd])



  function getFilteredEvents() {
    const sorted_events = multiworldEvents
      .filter((x: Event) => x.timestamp)
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      )
    return sorted_events.filter(event => {
      const { from_player, to_player } = event
      const event_type = event["event_type"] as string

      // Filter events
      if (
        // System events
        ([
          "init_success",
          "player_join",
          "player_leave",
          "player_forfeit",
          "player_pause_receive",
          "player_resume_receive",
          "session_create",
        ].includes(event_type) &&
          !showSystem) ||
        // Item filters
        (event_type === "new_item" &&
          currentPlayer &&
          // Self items
          ((to_player === currentPlayer && !showSelfItems) ||
            // Others items
            (to_player !== currentPlayer && !showOtherItems) ||
            // Same player items
            from_player == -1 ||
            (from_player == to_player && !showSamePlayerItems))) ||
        // Chat messages
        (event_type === "chat" && !showChat) ||
        // Filter out ready check ephemeral events
        (event_type === "chat" && event.event_data &&
          ["ready_check", "ready_response", "unready_response", "ready_check_cancel"].includes(event.event_data.type))
      ) {
        return false
      }
      return true
    })
  }

  let filteredEvents = getFilteredEvents()

  filteredEvents = filteredEvents.reduce((acc: Event[], x: Event) => {
    const id = x.id
    const historical = x.event_historical
    if (
      !acc.some(
        (item: Event) => item.id === id && item.event_historical === historical,
      )
    ) {
      acc.push(x)
    }
    return acc
  }, [])

  const eventContainerRef = useRef<HTMLDivElement>(null!)
  const chatInputRef = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!hasScrolled) {
      bottomRef.current?.scrollIntoView({ block: "end" })
    }
  }, [multiworldEvents, hasScrolled])

  useEffect(() => {
    if (eventsError && "status" in eventsError && eventsError.status === 403) {
      dispatch(
        addEvent({
          event_type: "chat",
          from_player: -1,
          to_player: -1,
          timestamp: Date.now(),
          event_data: {
            message: `Your connection to the server was closed (Authorized users only).`,
            type: "error",
          },
          id: nanoid(),
        }),
      )
      dispatch(
        addEvent({
          event_type: "chat",
          from_player: -1,
          to_player: -1,
          timestamp: Date.now(),
          event_data: {
            message: `Redirecting to home page...`,
            type: "error",
          },
          id: nanoid(),
        }),
      )
      setTimeout(() => {
        window.location.href = "/"
      }, 2000)
    }
  }, [eventsError, dispatch])

  const handleOnScroll = () => {
    if (!eventContainerRef.current) {
      return
    }
    const { clientHeight, scrollTop, scrollHeight } = eventContainerRef.current
    const atBottom = scrollHeight - clientHeight - scrollTop < 2
    setHasScrolled(!atBottom)
  }

  const handleScrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ block: "end" })
    setHasScrolled(false)
  }

  function handleChatSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (!chatMessage) {
      return
    }
    dispatch(sendChatMessage({ message: chatMessage, user_id: user.id }))
    setChatMessage("")
    setSelectedCommandIndex(0)
  }

  function selectCommand(command: typeof allCommands[number]) {
    const hasParams = command.params.length > 0
    // Currently always add a space so the helper message shows, but we could be smarter about this in the future
    setChatMessage(command.name + (hasParams ? " " : " "))
    setSelectedCommandIndex(0)
    setPopupDismissed(true)
    chatInputRef.current?.focus()
  }

  function handleChatKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!isSelectingCommand || filteredCommands.length === 0) return
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedCommandIndex(i => (i - 1 + filteredCommands.length) % filteredCommands.length)
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedCommandIndex(i => (i + 1) % filteredCommands.length)
    } else if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault()
      selectCommand(filteredCommands[selectedCommandIndex])
    }
  }

  const allEventsFiltered = () => {
    return (
      !showSelfItems &&
      !showSamePlayerItems &&
      !showOtherItems &&
      !showChat &&
      !showSystem
    )
  }

  const canSendMessages = (initComplete && currentPlayer && currentPlayer >= 0) || user.discordUsername

  const commandPrefix = chatMessage.split(" ")[0].toLowerCase()
  const isSelectingCommand = chatMessage.startsWith("/") && !chatMessage.includes(" ") && canSendMessages && !popupDismissed
  const matchedCommand = chatMessage.includes(" ")
    ? allCommands.find(cmd => cmd.name === commandPrefix && !cmd.disabled)
    : null
  const filteredCommands = isSelectingCommand
    ? allCommands.filter(cmd => cmd.name.startsWith(commandPrefix) && !cmd.disabled)
    : []
  const showCommandPopup = (isSelectingCommand && filteredCommands.length > 0) || matchedCommand !== null

  useEffect(() => {
    setSelectedCommandIndex(0)
  }, [commandPrefix])

  const renderEvents = useCallback((event: Event) => {
    const originalPlayerNames = players ? players.map((pnames: string[]) => pnames[1]) : []
    const finalPlayerNames = players ? players.map((pnames: string[]) => pnames[0]) : []
    return (
  
    <MultiEventText key={event.id} event={event} originalPlayerNames={originalPlayerNames} finalPlayerNames={finalPlayerNames}  />
  )}, [players])

  const renderedEvents = filteredEvents.map(renderEvents)

  return (
    <div className="flex flex-col max-w-6xl mt-2">
      <div className="h-72 rounded-md border relative">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              className={
                "h-8 w-8 absolute top-3 right-3 opacity-75 z-10" +
                (allEventsFiltered() ? " bg-red-500" : "")
              }
            >
              <Settings2Icon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="grid gap-4">
              <div className="space-y-2 ">
                <h4 className="font-medium leading-none">Filters</h4>
                <p className="text-sm text-muted-foreground">
                  Filter the messages shown.
                </p>
              </div>
              <div className="grid gap-2">
                <div className="grid grid-cols-5 items-center gap-1">
                  <Input
                    type="checkbox"
                    id="items_to_player"
                    className="col-span-1 h-4"
                    onChange={e => setShowSelfItems(e.target.checked)}
                    checked={showSelfItems}
                    disabled={currentPlayer === 0}
                  />
                  <Label htmlFor="items_to_player" className="col-span-4">
                    Items for you
                  </Label>
                </div>
                <div className="grid grid-cols-5 items-center gap-1">
                  <Input
                    type="checkbox"
                    id="items_to_others"
                    className="col-span-1 h-4"
                    onChange={e => setShowOtherItems(e.target.checked)}
                    checked={showOtherItems}
                    disabled={currentPlayer === 0}
                  />
                  <Label htmlFor="items_to_others" className="col-span-4">
                    Items for other players
                  </Label>
                </div>
                <div className="grid grid-cols-5 items-center gap-1">
                  <Input
                    type="checkbox"
                    id="chat_msg"
                    className="col-span-1 h-4"
                    onChange={e => setShowChat(e.target.checked)}
                    checked={showChat}
                  />
                  <Label htmlFor="chat_msg" className="col-span-4">
                    Chat messages
                  </Label>
                </div>
                <div className="grid grid-cols-5 items-center gap-1">
                  <Input
                    type="checkbox"
                    id="system"
                    className="col-span-1 h-4"
                    onChange={e => setShowSystem(e.target.checked)}
                    checked={showSystem}
                  />
                  <Label htmlFor="system" className="col-span-4">
                    System messages
                  </Label>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {isLoading || playersLoading || !multiworldEvents ? (
          <div>Loading... ({isLoading})</div>
        ) : (
          <ScrollArea onScroll={handleOnScroll} scrollPrimitiveRef={eventContainerRef} className="h-full w-full">
            {renderedEvents}
            <div ref={bottomRef} />
          </ScrollArea>
            )
        }
        {hasScrolled && (
          <Button
            className="flex flex-col items-center space-y-4 text-sm absolute bottom-3 right-3 z-10 h-8 opacity-60"
            onClick={handleScrollToBottom}
          >
            Scroll to bottom
          </Button>
        )}
      </div>
      <form
        id="chatBox"
        className="h-8 mt-2 rounded-md flex flex-row relative"
        onSubmit={handleChatSubmit}
      >
        {showCommandPopup && (
          <div className="absolute bottom-full mb-1 left-0 w-full rounded-md border bg-popover p-1 shadow-md z-20">
            {matchedCommand ? (
              <div className="flex w-full items-start gap-2 px-2 py-1.5 text-sm">
                <span className="font-mono font-medium shrink-0">{matchedCommand.name}</span>
                {matchedCommand.params && <span className="text-muted-foreground font-mono">{matchedCommand.params}</span>}
                <span className="text-muted-foreground ml-auto truncate">{matchedCommand.description}</span>
              </div>
            ) : (
              filteredCommands.map((cmd, i) => (
                <button
                  key={cmd.name}
                  type="button"
                  className={
                    "flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-sm text-left cursor-pointer " +
                    (i === selectedCommandIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50")
                  }
                  onMouseDown={e => { e.preventDefault(); selectCommand(cmd) }}
                >
                  <span className="font-mono font-medium shrink-0">{cmd.name}</span>
                  {cmd.params && <span className="text-muted-foreground font-mono">{cmd.params}</span>}
                  <span className="text-muted-foreground ml-auto truncate">{cmd.description}</span>
                </button>
              ))
            )}
          </div>
        )}
        <Input
          ref={chatInputRef}
          type="text"
          className="h-8 rounded-md flex mr-1"
          disabled={!canSendMessages}
          placeholder={
            canSendMessages
              ? "Send a message..."
              : "Cannot send messages until connected or logged in with discord..."
          }
          value={chatMessage}
          onChange={e => { setChatMessage(e.target.value); setPopupDismissed(false) }}
          onKeyDown={handleChatKeyDown}
        />
        <Button className="h-8 w-1/12 rounded-md flex" disabled={!canSendMessages}>
          Send
        </Button>
      </form>
    </div>
  )
}

export default MultiEventViewer
